import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/gateways";
import useSEO from "@/hooks/useSEO";
import MinisterCredential from "@/components/MinisterCredential";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2
      className="font-sans-body text-[10px] tracking-[0.36em] uppercase mb-3"
      style={{ color: "var(--olive)" }}
    >
      {title}
    </h2>
    <div
      className="font-sans-body text-[14px] leading-relaxed space-y-4"
      style={{ color: "var(--ink)" }}
    >
      {children}
    </div>
  </section>
);

const MarkRow = ({ mark, kind, description }) => (
  <div
    className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 py-4 border-b border-[rgba(176,133,94,0.25)] last:border-0"
  >
    <div>
      <p
        className="font-serif-display text-xl leading-tight"
        style={{ color: "var(--ink)" }}
      >
        {mark}
        <sup className="tm">TM</sup>
      </p>
      <p
        className="font-sans-body text-[12px] italic mt-0.5"
        style={{ color: "var(--stone-deep)" }}
      >
        {description}
      </p>
    </div>
    <p
      className="font-sans-body text-[10px] tracking-[0.32em] uppercase whitespace-nowrap"
      style={{ color: "var(--olive)" }}
    >
      {kind}
    </p>
  </div>
);

const Trademarks = () => {
  useSEO({
    title: "Trademarks — Selah & Stone Legacy Holdings",
    description:
      "Trademarks and brand marks owned by Selah and Stone Legacy Holdings, LLC, including Selah Slate, Selah Downloads, Selah and Stone Research, Selah and Salt Home, Family Legacy Audits, Omni Signing Services, and The Selah Shift.",
    path: "/trademarks",
  });

  return (
    <div
      className="paper-grain relative min-h-screen"
      data-testid="trademarks-page"
    >
      <div className="relative z-10">
        {/* Top bar */}
        <div className="px-6 sm:px-10 pt-8">
          <div className="mx-auto max-w-3xl flex items-start justify-between">
            <Link to="/" className="leading-tight">
              <div
                className="font-sans-body text-[10px] sm:text-[11px] tracking-[0.32em] uppercase"
                style={{ color: "var(--ink)" }}
              >
                Selah &nbsp;&middot;&nbsp; Stone
              </div>
              <div
                className="font-sans-body text-[9px] sm:text-[10px] tracking-[0.42em] uppercase mt-1"
                style={{ color: "var(--olive)" }}
              >
                Legacy Holdings
              </div>
            </Link>
            <Link
              to="/"
              className="font-sans-body text-[11px] tracking-[0.22em] uppercase pt-1 underline hover:no-underline"
              style={{ color: "var(--stone-deep)" }}
              data-testid="trademarks-back-link"
            >
              Back
            </Link>
          </div>
        </div>

        {/* Header */}
        <header className="px-6 sm:px-10 pt-16 pb-10">
          <div className="mx-auto max-w-3xl">
            <img
              src={LOGO_URL}
              alt="Selah and Stone Legacy Holdings"
              className="h-16 w-16 mb-6"
            />
            <p
              className="font-sans-body text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "var(--olive)" }}
            >
              Trademarks
            </p>
            <h1
              className="mt-3 font-serif-display text-5xl sm:text-6xl font-light leading-[1]"
              style={{ color: "var(--ink)" }}
            >
              Our marks,
              <span
                className="block italic"
                style={{ color: "var(--olive)" }}
              >
                honored quietly.
              </span>
            </h1>
            <p
              className="mt-6 font-serif-display italic text-xl max-w-2xl"
              style={{ color: "var(--stone-deep)" }}
            >
              The names, logos, and phrases below identify Selah and Stone
              Legacy Holdings and our family of brands.
            </p>
          </div>
        </header>

        {/* Body */}
        <article className="px-6 sm:px-10 pb-20">
          <div className="mx-auto max-w-3xl">
            <Section title="Ownership and reservation of rights">
              <p>
                All trademarks, service marks, trade names, logos, seals,
                wordmarks, taglines, and trade dress displayed on this site
                are the property of <strong>Selah and Stone Legacy Holdings,
                LLC</strong> and are protected under United States federal
                trademark law, common law, and applicable state law.
              </p>
              <p>
                The absence of a &trade;, &reg;, or SM symbol next to any
                mark is not a waiver of our rights. Applications may be
                pending or in preparation. We reserve all rights, including
                the right to prevent confusion, dilution, and unauthorized
                association.
              </p>
            </Section>

            <Section title="Our trademarks">
              <p>
                The following are trademarks of{" "}
                <strong>Selah and Stone Legacy Holdings, LLC</strong>,
                whether or not they are accompanied by the &trade; symbol on
                any given page. Unauthorized use of these marks — including
                in domain names, product names, or marketing that could
                suggest affiliation or sponsorship — is not permitted.
              </p>
              <div className="mt-4">
                <MarkRow
                  mark="Selah and Stone Legacy Holdings"
                  kind="House Mark"
                  description="Full legal wordmark of the LLC and its family of brands."
                />
                <MarkRow
                  mark="Selah Slate"
                  kind="Product"
                  description="Free financial-wellness tool at app.selahslate.com."
                />
                <MarkRow
                  mark="Selah Downloads"
                  kind="Product"
                  description="Spiritual-guidance app for capturing and walking out divine direction."
                />
                <MarkRow
                  mark="Selah and Stone Research"
                  kind="Service"
                  description="Background screening and open-source forensic due diligence."
                />
                <MarkRow
                  mark="Selah and Salt Home"
                  kind="Brand"
                  description="Culinary hub for generational recipes and wellness rhythms."
                />
                <MarkRow
                  mark="Family Legacy Audits"
                  kind="Service · Coming Soon"
                  description="Deep-dive diagnostic for a family's spiritual and structural legacy."
                />
                <MarkRow
                  mark="Omni Signing Services"
                  kind="Service"
                  description="Notary public and loan signing services."
                />
                <MarkRow
                  mark="The Selah Shift"
                  kind="Title"
                  description="Forthcoming book documenting our journey from guidance to restoration."
                />
                <MarkRow
                  mark="Selah and Stone Legacy Foundation"
                  kind="Foundation"
                  description="501(c)(3) coming soon."
                />
              </div>
            </Section>

            <Section title="Taglines and phrases">
              <p>
                The following taglines and gateway phrases are also owned by
                Selah and Stone Legacy Holdings, LLC:
              </p>
              <ul className="list-disc pl-5 space-y-1 pt-1">
                <li>Restoring the Foundations of the Modern Family.</li>
                <li>One Family &middot; Breaking Curses &middot; Building Legacy.</li>
                <li>Stop. Restore. Rebuild.</li>
                <li>Pause. Listen. Receive.</li>
                <li>Truth. Foundation. Forensics.</li>
                <li>Gather. Nourish. Preserve.</li>
                <li>Audit your Foundation.</li>
                <li>Accuracy. Integrity. Professionalism.</li>
                <li>The Work was done in the Selah.</li>
              </ul>
            </Section>

            <Section title="Trade dress and visual identity">
              <p>
                Our overall visual identity — including the specific
                combination of parchment (#F5E8E0), slate blue (#3F5468),
                and copper (#B0855E); the Cormorant Garamond wordmark
                lockup; the circular seal composition; and the editorial
                layout of the &ldquo;switchboard&rdquo; homepage — is
                proprietary trade dress of Selah and Stone Legacy Holdings,
                LLC. Copying or substantially imitating this presentation
                for a competing or related service is prohibited.
              </p>
            </Section>

            <Section title="Permitted (nominative) use">
              <p>
                You may refer to our brands by name in accurate, factual
                editorial or referential contexts — for example, in a news
                article, review, or podcast episode discussing our work.
                Nominative use must:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Use only the wording necessary to identify us;</li>
                <li>
                  Not imply sponsorship, endorsement, affiliation, or
                  partnership that does not exist;
                </li>
                <li>
                  Not alter our logos, seal, or wordmark (no recoloring,
                  cropping, distorting, or adding effects);
                </li>
                <li>
                  Not appear in a way likely to cause confusion with our
                  own goods or services.
                </li>
              </ul>
            </Section>

            <Section title="Logo and visual identity">
              <p>
                The Selah and Stone seal, the wordmark set in Cormorant
                Garamond, and the parchment/slate/copper brand palette are
                proprietary elements of Selah and Stone Legacy Holdings,
                LLC. You may not reproduce, modify, or use them without our
                prior written permission.
              </p>
              <p>
                Approved brand assets (transparent PNG, square social
                versions, print-ready, and SVG) are available in our brand
                kit at{" "}
                <a
                  href="/brand-kit/selah-and-stone-brand-kit.zip"
                  className="underline"
                  style={{ color: "var(--olive)" }}
                  data-testid="trademarks-brand-kit-link"
                >
                  /brand-kit/selah-and-stone-brand-kit.zip
                </a>
                . Use of the brand kit is limited to accurate reference to
                Selah and Stone and its family of brands.
              </p>
            </Section>

            <Section title="Third-party marks">
              <p>
                All other trademarks, service marks, product names, and
                company names or logos referenced on this site (including
                Google Play, Resend, Squarespace, MongoDB, PostHog, and any
                other named third-party services) are the property of their
                respective owners. Reference to them does not imply
                affiliation, endorsement, or sponsorship.
              </p>
              <p>
                Selah and Stone Legacy Holdings is <em>not</em> affiliated
                with any textile or home goods companies outside of our
                family of brands.
              </p>
            </Section>

            <Section title="Prohibited use">
              <p>
                Except as expressly permitted above or by prior written
                license from Selah and Stone Legacy Holdings, LLC, you may
                not:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Register or use any of our marks (or confusingly similar
                  variants) as a domain name, social handle, business name,
                  product name, or trade name;
                </li>
                <li>
                  Use our marks in metadata, keywords, hidden text, or paid
                  advertising in a way that misleads consumers about
                  source, sponsorship, or affiliation;
                </li>
                <li>
                  Sell, license, or distribute merchandise bearing our
                  marks;
                </li>
                <li>
                  Use our marks in a manner that disparages us, our brands,
                  or our clients, or that is otherwise defamatory,
                  obscene, or unlawful.
                </li>
              </ul>
            </Section>

            <Section title="Enforcement">
              <p>
                Unauthorized use of our marks is a serious matter. We
                actively monitor for infringement and reserve the right to
                pursue all available remedies, including cease-and-desist
                demands, takedown notices to platforms, domain-name
                proceedings under the UDRP or ACPA, and civil litigation
                for trademark infringement, unfair competition, dilution,
                and false designation of origin under the Lanham Act
                (15 U.S.C. &sect;&sect; 1051 <em>et seq.</em>) and
                corresponding state law.
              </p>
            </Section>

            <Section title="Reporting misuse">
              <p>
                If you believe someone is using our marks without permission
                — including a domain, social handle, product, or content
                that could suggest an affiliation we did not authorize —
                please write to{" "}
                <a
                  href="mailto:admin@selahandstonelegacy.com"
                  className="underline"
                  style={{ color: "var(--olive)" }}
                  data-testid="trademarks-contact-link"
                >
                  admin@selahandstonelegacy.com
                </a>
                . Include a link and a brief description of the concern,
                and we will investigate.
              </p>
            </Section>

            <Section title="Licensing">
              <p>
                Requests to license, co-brand, or otherwise use any Selah
                and Stone mark should be sent to{" "}
                <a
                  href="mailto:admin@selahandstonelegacy.com"
                  className="underline"
                  style={{ color: "var(--olive)" }}
                >
                  admin@selahandstonelegacy.com
                </a>{" "}
                with &ldquo;Trademark Licensing&rdquo; in the subject line.
              </p>
              <p className="pt-2 font-serif-display italic" style={{ color: "var(--stone-deep)" }}>
                Selah and Stone Legacy Holdings, LLC
                <br />
                Registered in Hillsborough County, Florida, USA
              </p>
            </Section>

            <div className="rule-ornament mt-16">
              <span
                className="font-serif-display italic text-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                selah
              </span>
            </div>

            <MinisterCredential />
          </div>
        </article>
      </div>
    </div>
  );
};

export default Trademarks;
